import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import useAccessibleOverlay from "../hooks/useAccessibleOverlay";

const STORAGE_KEY = "confirmph_cookie_pref_v1";
// Any part of the app can reopen the preferences panel by dispatching this
// event; the footer link does exactly that. Kept as an event rather than a
// context so nothing else has to be wrapped in a provider.
export const OPEN_PREFERENCES_EVENT = "confirmph:cookie-preferences";

export function openCookiePreferences() {
  window.dispatchEvent(new CustomEvent(OPEN_PREFERENCES_EVENT));
}

function getStored() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null"); }
  catch { return null; }
}

// Task 8 cookie audit: the analytics toggle has to actually control the
// analytics. PostHog is initialised in index.html, so without this the
// visitor's choice was recorded and then ignored, which is worse than not
// asking. Safe if the snippet never loaded (local dev skips it).
export function applyAnalyticsChoice(allowed) {
  try {
    const ph = typeof window !== "undefined" ? window.posthog : null;
    if (!ph) return;
    if (allowed) ph.opt_in_capturing?.();
    else {
      ph.opt_out_capturing?.();
      ph.stopSessionRecording?.();
    }
  } catch { /* analytics must never break the page */ }
}

export default function CookieBanner() {
  const { user } = useAuth() || {};
  const [pref, setPref] = useState(getStored());
  const [showManage, setShowManage] = useState(false);
  const { panelRef, triggerRef } = useAccessibleOverlay(showManage, () => setShowManage(false));
  // categories: essential is always on; analytics on by default; marketing always off
  const [categories, setCategories] = useState({
    essential: true,
    preferences: true,
    analytics: true,
    marketing: false,
  });

  // Reopen on request from anywhere in the app. The panel must show what
  // the visitor actually chose, not the first-visit defaults: someone who
  // picked "Essential only" would otherwise see analytics switched on and
  // could save it that way without meaning to.
  useEffect(() => {
    const open = () => {
      const saved = getStored();
      if (saved) {
        setCategories({
          essential: true,
          preferences: saved.preferences !== false,
          analytics: !!saved.analytics,
          marketing: !!saved.marketing,
        });
      }
      setShowManage(true);
    };
    window.addEventListener(OPEN_PREFERENCES_EVENT, open);
    return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, open);
  }, []);

  // Enforce the stored choice on every load, not just at the moment it is
  // made: a returning visitor who chose "Essential only" must stay opted out.
  useEffect(() => {
    const saved = getStored();
    if (saved) applyAnalyticsChoice(!!saved.analytics);
  }, []);

  // If the visitor logs in and we have a local cookie consent that hasn't
  // been synced to the backend, push it once. Tracked via a flag inside
  // the local-storage record so we never spam the API.
  useEffect(() => {
    if (!user || !pref || pref.synced_user_id === user.id) return;
    api.post("/account/cookie-consent", {
      accepted: true,
      version: pref.version || 1,
      categories: {
        essential: !!pref.essential,
        preferences: pref.preferences !== false,
        analytics: !!pref.analytics,
        marketing: !!pref.marketing,
      },
    })
      .then(() => {
        const next = { ...pref, synced_user_id: user.id };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        setPref(next);
      })
      .catch(() => { /* best-effort */ });
  }, [user, pref]);

  // Reopening is always available; the banner itself is shown only until a
  // choice has been made.
  if (pref && !showManage) return null;

  const persist = (cats) => {
    const next = {
      ...cats,
      essential: true,
      timestamp: new Date().toISOString(),
      version: 1,
      synced_user_id: null,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setPref(next);
    applyAnalyticsChoice(!!cats.analytics);
    if (user) {
      // Fire-and-forget: local copy is the source of truth for unauth users.
      api.post("/account/cookie-consent", {
        accepted: true, version: 1,
        categories: {
          essential: true,
          preferences: !!cats.preferences,
          analytics: !!cats.analytics,
          marketing: !!cats.marketing,
        },
      })
        .then(() => {
          const synced = { ...next, synced_user_id: user.id };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(synced));
          setPref(synced);
        })
        .catch(() => { /* ignore */ });
    }
  };

  const acceptAll = () => persist({ ...categories, analytics: true, marketing: false });
  const acceptEssentialOnly = () => persist({ essential: true, preferences: false, analytics: false, marketing: false });
  const saveCustom = () => { persist(categories); setShowManage(false); };

  return (
    <>
      {/* Banner: only until a choice has been made. */}
      {!pref && (
      <div data-testid="cookie-banner"
            className="fixed bottom-3 left-3 right-3 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md z-50">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-xl p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <Cookie className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-slate-700 leading-relaxed">
              <p>
                ConfirmPH uses essential cookies and local storage to keep you signed in
                and secure. We also use PostHog product analytics to understand usage
                and fix bugs. We do <b>not</b> use advertising or cross-site tracking.
              </p>
              <p className="mt-1 text-xs text-slate-500">
                See our <Link to="/cookies" className="underline" data-testid="cookie-banner-cookies-link">Cookie Policy</Link> for details.
              </p>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={acceptAll}
              data-testid="cookie-accept-all"
              className="px-3 py-1.5 rounded-lg btn-brand text-sm font-medium">
              Accept all
            </button>
            <button
              onClick={acceptEssentialOnly}
              data-testid="cookie-accept-essential"
              className="px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm font-medium">
              Essential only
            </button>
            <button
              ref={triggerRef}
              onClick={() => setShowManage(true)}
              data-testid="cookie-manage"
              className="px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm font-medium">
              Manage preferences
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Manage modal */}
      {showManage && (
        <div className="fixed inset-0 z-[60] overflow-y-auto bg-black/40"
             onClick={() => setShowManage(false)}
             data-testid="cookie-manage-modal">
          <div className="min-h-full flex items-center justify-center p-4">
            <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby="cookie-preferences-title" tabIndex={-1}
                 className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl my-4"
                 onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-2">
              <h2 id="cookie-preferences-title" className="font-display text-xl font-semibold">Cookie preferences</h2>
              <button type="button" onClick={() => setShowManage(false)} aria-label="Close cookie preferences"
                       data-testid="cookie-manage-close"
                       className="text-slate-500 hover:text-slate-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <ul className="mt-4 space-y-3 text-sm">
              <ToggleRow title="Strictly necessary" required
                    desc="Authentication, session, security. Required for the app to work."
                    value />
              <ToggleRow title="Preferences"
                    desc="Remember non-essential choices (last-used tab, filters)."
                    value={categories.preferences}
                    onChange={(v) => setCategories({ ...categories, preferences: v })}
                    testId="cookie-cat-preferences" />
              <ToggleRow title="Analytics"
                    desc="PostHog product analytics: anonymous + hashed-identifier events. No advertising trackers."
                    value={categories.analytics}
                    onChange={(v) => setCategories({ ...categories, analytics: v })}
                    testId="cookie-cat-analytics" />
              <ToggleRow title="Marketing" disabled
                    desc="No advertising trackers are used on ConfirmPH."
                    value={false} />
            </ul>
            <button
              onClick={saveCustom}
              data-testid="cookie-manage-save"
              className="mt-5 w-full px-3 py-2 rounded-lg btn-brand text-sm font-medium">
              Save preferences
            </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ToggleRow({ title, desc, required, disabled, value, onChange, testId }) {
  const locked = required || disabled;
  return (
    <li className="flex items-start gap-3">
      <button
        type="button"
        disabled={locked}
        onClick={() => !locked && onChange && onChange(!value)}
        data-testid={testId}
        aria-pressed={!!value}
        className={`mt-0.5 inline-flex items-center w-10 h-5 rounded-full transition-colors ${
          value ? "bg-emerald-500" : "bg-slate-300"
        } ${locked ? "opacity-60 cursor-not-allowed" : ""}`}>
        <span className={`w-4 h-4 rounded-full bg-white shadow transform transition-transform ${
          value ? "translate-x-5" : "translate-x-0.5"
        }`} />
      </button>
      <div>
        <div className="font-medium text-slate-900">{title}
          {required && <span className="ml-1 text-[10px] uppercase tracking-[0.18em] text-emerald-600">Required</span>}
          {disabled && <span className="ml-1 text-[10px] uppercase tracking-[0.18em] text-slate-500">Disabled</span>}
        </div>
        <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
      </div>
    </li>
  );
}
